import React, { useCallback, useEffect, useMemo, useRef, useState, type SVGProps } from 'react';
import Image from 'next/image';
import { cx } from '@/utils';
import CustomLink from '@common/CustomLink';
import SvgCaret from '@common/Icons/Caret';
import {
  FileInfo,
  Maybe,
  RouterType,
  type NavigationItem,
  type PageLink,
} from '@common/Providers/GraphqlProvider/generated';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';

import styles from './styles.module.css';

type Props = {
  title: string;
  icon?: React.FC<SVGProps<any>>;
  selected: boolean;
  route?: string | undefined;
  items?: NavigationItem[];
  itemLink?: PageLink | null;
  signLanguageGif?: Maybe<FileInfo>;
  uniqueId: string;
  isSticky?: boolean;
  className?: string;
};

export function getSorterdArrayNavigationItems(
  items: NavigationItem[] | undefined
): NavigationItem[] {
  if (!items) {
    return [];
  }
  const sortedArray = Object.values(items).sort((a, b) => a.orderIndex - b.orderIndex);
  return sortedArray;
}

const tooltipStyles = {
  backgroundColor: 'white',
  color: 'black',
  border: '1px solid #e5e7eb',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  padding: '12px',
  opacity: '1',
};

const LinkNavigation = ({
  icon,
  title,
  selected,
  items,
  itemLink,
  route = '#',
  uniqueId,
  signLanguageGif,
  isSticky = false,
  className,
}: Props) => {
  const sortedItems = getSorterdArrayNavigationItems(items);
  const [hover, setHover] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const iconRender = useMemo(() => {
    if (typeof icon === 'string') {
      return <Image src={icon} alt={title} width={16} height={12} />;
    }
    if (typeof icon === 'function')
      return React.createElement(icon, {
        className: cx(selected || hover ? 'text-white' : 'text-tundora'),
      });
    return null;
  }, [hover, icon, selected, title]);

  // Calculate dropdown position when opening
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  // Sync dropdown position on scroll and resize
  useEffect(() => {
    if (!isOpen) return;

    let ticking = false;

    const handleScroll = (e: Event) => {
      // Don't update if scrolling inside the dropdown itself
      if (
        dropdownRef.current &&
        (e.target === dropdownRef.current || dropdownRef.current.contains(e.target as Node))
      ) {
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateDropdownPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Use capture phase to catch all scrolls in the document
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updateDropdownPosition);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when focus leaves the container (dropdown is inside container now)
  const handleBlur = useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!containerRef.current?.contains(relatedTarget)) {
      setIsOpen(false);
      setHover(false);
      setActiveSubmenu(null);
    }
  }, []);

  const openMenu = useCallback(() => {
    updateDropdownPosition();
    setIsOpen(true);
    setHover(true);
  }, [updateDropdownPosition]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHover(false);
    setActiveSubmenu(null);
  }, []);

  // Handle keyboard on trigger
  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
        case 'ArrowDown':
          e.preventDefault();
          openMenu();
          // Focus first item in dropdown after render
          setTimeout(() => {
            const firstItem = dropdownRef.current?.querySelector<HTMLElement>('[data-nav-item]');
            firstItem?.focus();
          }, 50);
          break;
        case 'Escape':
          closeMenu();
          break;
      }
    },
    [openMenu, closeMenu]
  );

  // Handle keyboard inside dropdown
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        triggerRef.current?.focus();
      }
    },
    [closeMenu]
  );

  if (items && items.length) {
    const hasParentLink = itemLink && itemLink.routeType !== RouterType.NoAction;

    const triggerContent = (
      <>
        <div className="relative flex min-w-0 flex-1 items-center">
          {iconRender}
          <div className="relative ml-2 flex-1">
            <span
              className={cx(
                'flex w-full items-center justify-start  gap-[0.25rem]  truncate font-workSans text-[12px] font-medium not-italic leading-[16px] transition-colors duration-200 md:group-hover:text-white md:group-hover:underline lg:text-[13px] lg:leading-[18px] xl:text-[14px] xl:leading-[21px]',
                selected || hover ? 'text-white' : 'text-tundora'
              )}
              title={title}
            >
              {title}
            </span>
          </div>
        </div>
        <SvgCaret
          className={cx(
            'min-w-[10px] flex-shrink-0 transition-colors duration-200',
            selected || hover ? 'text-white' : 'text-tundora'
          )}
        />
      </>
    );

    return (
      <>
        <div
          ref={containerRef}
          className={cx(
            'flex h-full flex-[1_1_auto] cursor-pointer select-none flex-row items-center space-x-2 border-none lg:hover:bg-primary',
            { ['bg-primary']: selected || hover },
            className
          )}
          data-tooltip-id={signLanguageGif ? `tooltip-${uniqueId}` : undefined}
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
          onBlurCapture={handleBlur}
        >
          {hasParentLink ? (
            <CustomLink
              className="has-link h-full w-full"
              pageLink={itemLink}
              href={route}
              tabIndex={-1}
            >
              <button
                ref={triggerRef}
                className="group flex h-full w-full min-w-24 cursor-pointer items-center justify-between gap-1 border-none bg-transparent px-4 py-4 outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white"
                type="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={isOpen}
                onFocus={openMenu}
                onKeyDown={handleTriggerKeyDown}
              >
                {triggerContent}
              </button>
            </CustomLink>
          ) : (
            <button
              ref={triggerRef}
              className="group flex h-full w-full min-w-24 cursor-pointer items-center justify-between gap-1 border-none bg-transparent px-4 py-4 outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white"
              type="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={isOpen}
              onFocus={openMenu}
              onKeyDown={handleTriggerKeyDown}
            >
              {triggerContent}
            </button>
          )}

          {/* Dropdown - inside container for natural mouse events, position:fixed to escape overflow clipping */}
          {isOpen && sortedItems.length > 0 && (
            <div
              ref={dropdownRef}
              className={cx(
                styles.menu,
                'fixed z-[9999] animate-[fadeSlideDown_150ms_ease-out] overflow-y-auto overflow-x-hidden bg-white'
              )}
              style={{
                top: dropdownPos.top,
                left: dropdownPos.left,
                minWidth: Math.max(dropdownPos.width, 220),
                maxWidth: 320,
                maxHeight: `calc(100vh - ${dropdownPos.top + 20}px)`,
              }}
              role="menu"
              aria-label={`Submenú de ${title}`}
              onKeyDown={handleDropdownKeyDown}
            >
              {sortedItems.map((item, index) => (
                <DropdownItem
                  key={index}
                  item={item}
                  index={index}
                  parentLink={itemLink}
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sign language tooltip */}
        {signLanguageGif &&
          isMounted &&
          createPortal(
            <Tooltip
              id={`tooltip-${uniqueId}`}
              style={tooltipStyles}
              place={isSticky ? 'left' : 'top'}
              className="z-overlay"
            >
              <div className="flex flex-col items-center">
                <img
                  src={signLanguageGif.downloadUrl || ''}
                  alt={`Lengua de señas para ${title}`}
                  width={70}
                  height={70}
                  className="rounded-md"
                />
              </div>
            </Tooltip>,
            document.body
          )}
      </>
    );
  }

  // Leaf item (no children) — simple link
  return (
    <>
      <CustomLink
        className={cx(
          styles.linkNavigation,
          { ['bg-primary']: selected },
          'group flex flex-1 px-4 py-4',
          className
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        pageLink={itemLink}
        href={route}
        tabIndex={0}
        data-tooltip-id={signLanguageGif ? `tooltip-${uniqueId}` : undefined}
      >
        <div className="relative flex w-full min-w-0 items-center">
          {iconRender}
          <div className="relative ml-2 flex-1">
            <div
              className={cx(
                'flex w-full items-center justify-between gap-[0.25rem] truncate font-workSans text-[12px] font-medium not-italic leading-[16px] transition-colors duration-200 md:group-hover:text-white md:group-hover:underline lg:text-[13px] lg:leading-[18px] xl:text-[14px] xl:leading-[21px]',
                selected
                  ? 'text-white'
                  : 'text-tundora group-focus-within:text-white group-focus-visible:text-white'
              )}
              title={title}
            >
              {title}
            </div>
          </div>
        </div>
      </CustomLink>
      {signLanguageGif &&
        isMounted &&
        createPortal(
          <Tooltip
            id={`tooltip-${uniqueId}`}
            style={tooltipStyles}
            place={'top'}
            className="z-overlay"
          >
            <div className="flex flex-col items-center">
              <img
                src={signLanguageGif.downloadUrl || ''}
                alt={`Lengua de señas para ${title}`}
                width={80}
                height={80}
                className="rounded-md"
              />
            </div>
          </Tooltip>,
          document.body
        )}
    </>
  );
};

// ============================================
// DropdownItem — Renders a level-2 item
// ============================================
const DropdownItem = ({
  item,
  index,
  parentLink,
  activeSubmenu,
  setActiveSubmenu,
}: {
  item: NavigationItem;
  index: number;
  parentLink?: PageLink | null;
  activeSubmenu: number | null;
  setActiveSubmenu: (idx: number | null) => void;
}) => {
  const sortedChildren = getSorterdArrayNavigationItems(item.children);
  const hasChildren = sortedChildren.length > 0;
  const hasLink = item.linkItem && item.linkItem.routeType !== RouterType.NoAction;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [subPanelSide, setSubPanelSide] = useState<'right' | 'left'>('right');
  const [subPanelStyle, setSubPanelStyle] = useState<React.CSSProperties>({});

  // Detect if sub-panel would overflow the viewport on the right
  const updateSubPanelPosition = useCallback(() => {
    if (activeSubmenu === index && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const subPanelWidth = 230; // estimated sub-panel width
      const isLeft = rect.right + subPanelWidth > window.innerWidth;
      const estimatedHeight = Math.min(sortedChildren.length * 45 + 16, window.innerHeight - 40);
      // Determine how high we MUST push the menu up to not hit the bottom bounds
      let calculatedTop = rect.top;

      const bottomSpaceAvailable = window.innerHeight - rect.top;
      if (estimatedHeight > bottomSpaceAvailable - 20) {
        // We need to shift it up.
        // It should shift up exactly as much as it overflows, but never go higher than 20px from screen top
        const requiredShift = estimatedHeight - (bottomSpaceAvailable - 20);
        calculatedTop = Math.max(20, rect.top - requiredShift);
      }

      setSubPanelSide(isLeft ? 'left' : 'right');
      setSubPanelStyle({
        top: calculatedTop,
        left: isLeft ? 'auto' : rect.right + 2,
        right: isLeft ? window.innerWidth - rect.left + 2 : 'auto',
        maxHeight: window.innerHeight - calculatedTop - 20,
      });
    }
  }, [activeSubmenu, index, sortedChildren.length]);

  useEffect(() => {
    updateSubPanelPosition();
  }, [updateSubPanelPosition]);

  // Sync sub-panel position on scroll and resize
  useEffect(() => {
    if (activeSubmenu !== index) return;

    let ticking = false;

    const handleScroll = (e: Event) => {
      // Don't update if scrolling inside a sub-panel
      if (wrapperRef.current && wrapperRef.current.contains(e.target as Node)) {
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateSubPanelPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updateSubPanelPosition);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateSubPanelPosition);
    };
  }, [activeSubmenu, index, updateSubPanelPosition]);

  const isActive = activeSubmenu === index;

  // Leaf item at level 2 (no children)
  if (!hasChildren) {
    return (
      <CustomLink
        href={item.link || ''}
        target={'_blank'}
        pageLink={item.linkItem}
        parentLink={parentLink}
        className="block w-full"
        tabIndex={0}
        data-nav-item
        role="menuitem"
        onFocus={() => setActiveSubmenu(null)}
        onMouseEnter={() => setActiveSubmenu(null)}
      >
        <div className="group relative w-full px-4 py-3 font-workSans text-[14px] leading-[21px] transition-colors duration-150 hover:bg-gray-100">
          <span className="block w-full whitespace-normal break-words transition-all duration-150 group-hover:underline">
            {item.name}
          </span>
        </div>
      </CustomLink>
    );
  }

  // Item with children — has a sub-panel
  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setActiveSubmenu(index)}
      onFocusCapture={() => setActiveSubmenu(index)}
    >
      {/* Level 2 heading */}
      {hasLink ? (
        <CustomLink
          href={item.link || ''}
          pageLink={item.linkItem}
          parentLink={parentLink}
          className="block w-full"
          tabIndex={0}
          data-nav-item
          role="menuitem"
        >
          <div
            className={cx(
              'group relative flex w-full items-center justify-between px-4 py-3 font-workSans text-[14px] leading-[21px] transition-colors duration-150 hover:bg-gray-100',
              isActive && 'bg-gray-50'
            )}
          >
            <span
              className="block min-w-0 flex-1 whitespace-normal break-words transition-all duration-150 ease-in-out group-hover:underline"
              title={item.name}
            >
              {item.name}
            </span>
            <SvgCaret
              className={cx(
                'ml-2 w-[10px] flex-shrink-0 text-tundora transition-transform duration-150 ease-in-out',
                isActive ? 'rotate-0' : '-rotate-90'
              )}
            />
          </div>
        </CustomLink>
      ) : (
        <div
          tabIndex={0}
          data-nav-item
          role="menuitem"
          className={cx(
            'group relative flex w-full cursor-default items-center justify-between px-4 py-3 font-workSans text-[14px] leading-[21px] outline-none transition-colors duration-150 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
            isActive && 'bg-gray-50'
          )}
        >
          <span
            className="block min-w-0 flex-1 whitespace-normal break-words transition-all duration-150 ease-in-out"
            title={item.name}
          >
            {item.name}
          </span>
          <SvgCaret
            className={cx(
              'ml-2 w-[10px] flex-shrink-0 text-tundora transition-transform duration-150 ease-in-out',
              isActive ? 'rotate-0' : '-rotate-90'
            )}
          />
        </div>
      )}

      {/* Level 3 sub-panel */}
      {isActive && sortedChildren.length > 0 && (
        <div
          className={cx(
            'fixed z-[10000] min-w-[220px] max-w-[320px] animate-[fadeSlideIn_150ms_ease-out] rounded-md bg-white shadow-lg'
          )}
          style={{ ...subPanelStyle, display: 'flex', flexDirection: 'column' }}
          role="menu"
          aria-label={`Submenú de ${item.name}`}
        >
          {/* Invisible hover bridge to prevent menu from closing when moving mouse */}
          <div
            className="absolute bottom-0 top-0 w-8"
            style={{ [subPanelSide === 'left' ? 'right' : 'left']: '-20px' }}
          />
          <div
            className="overflow-y-auto overflow-x-hidden py-2"
            style={{ maxHeight: subPanelStyle.maxHeight }}
          >
            {sortedChildren.map((child, childIndex) => (
              <CustomLink
                key={childIndex}
                href={child.link || ''}
                target={'_blank'}
                pageLink={child.linkItem}
                parentLink={parentLink}
                subRouteName={item.name?.toLocaleLowerCase()}
                className="block w-full"
                tabIndex={0}
                data-nav-item
                role="menuitem"
              >
                <div className="group relative w-full px-4 py-3 font-workSans text-[14px] leading-[21px] transition-colors duration-150 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary">
                  <span className="block w-full whitespace-normal break-words transition-all duration-150">
                    {child.name}
                  </span>
                </div>
              </CustomLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkNavigation;
